import { clsx } from '@alexis/helpers/clsx';
import { translateNumber, translateText } from '@alexis/helpers/translation';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { HIDING_TRAVELLER_TYPE_SCORE, NEW_RATING_SCALE } from '@constants/hotels';

import { GlobalContext } from '@context/GlobalContext';

import {
  getNewReviewRatingType,
  getReviewRatingType,
  getReviewScoreDisplay,
} from '@helpers/review';

import SentimentDissatisfiedIcon from '@icons/sentiment_dissatisfied.svg';
import SentimentNeutralIcon from '@icons/sentiment_neutral.svg';
import SentimentSatisfied from '@icons/sentiment_satisfied.svg';

import { useReviewRatingType } from '@hooks/useReviewRatingType';

import { getLocaleState, getTranslationsState } from '@redux/selectors';

import { HotelAiReview } from '@wegoTypes/hotels/hotelAiReview';
import { HotelReview } from '@wegoTypes/hotels/hotelReview';

import ProgressRing from '../../../../components/ProgressRing';
import styles from './AiReviews.module.scss';

interface AiReviewsProps {
  className?: string;
  reviews: Array<HotelReview>;
  aiReviews: Array<HotelAiReview> | undefined;
}

const AiReviews: React.FC<AiReviewsProps> = ({ className, reviews, aiReviews }): JSX.Element => {
  const { userCentricCoreConfigs } = useContext(GlobalContext);
  const locale = useSelector(getLocaleState);
  const translations = useSelector(getTranslationsState);

  const { getReviewRatingTypeDisplay } = useReviewRatingType();

  const [selectedReview, setSelectedReview] = useState<HotelReview>(reviews[0]);

  const isHidingTravellerType =
    userCentricCoreConfigs.find((coreConfig) => coreConfig.key === HIDING_TRAVELLER_TYPE_SCORE)
      ?.value === '1';
  const isNewRatingScale =
    userCentricCoreConfigs.find((coreConfig) => coreConfig.key === NEW_RATING_SCALE)?.value === '1';

  const goodToKnowSnippets = useMemo<Array<HotelAiReview>>(() => {
    if (!!aiReviews && aiReviews.length) {
      return aiReviews;
    }
    return [];
  }, [aiReviews]);

  const getReviewRatingClassNames = (score: number) => {
    let backgroundColor = '';
    let typeColor = '';
    const ratingType = isNewRatingScale
      ? getNewReviewRatingType(score)
      : getReviewRatingType(score);

    switch (ratingType) {
      case 'EXCELLENT':
      case 'SUPERB':
      case 'VERYGOOD':
        backgroundColor = styles.backgroundExcellent;
        typeColor = styles.excellent;
        break;
      case 'GOOD':
        if (isNewRatingScale) {
          backgroundColor = styles.backgroundExcellent;
          typeColor = styles.excellent;
        } else {
          backgroundColor = styles.backgroundFair;
          typeColor = styles.fair;
        }
        break;
      case 'PLEASANT':
      case 'FAIR':
        if (isNewRatingScale) {
          backgroundColor = styles.backgroundFair;
          typeColor = styles.fair;
        } else {
          backgroundColor = styles.backgroundPoor;
          typeColor = styles.poor;
        }
        break;
      case 'POOR':
        backgroundColor = styles.backgroundPoor;
        typeColor = styles.poor;
        break;
    }

    return { backgroundColor, typeColor };
  };

  const { backgroundColor, typeColor } = getReviewRatingClassNames(selectedReview.score);

  const getReviewerGroupTranslation = useCallback<(reviewerGroup: string) => string>(
    (reviewerGroup: string) => {
      switch (reviewerGroup) {
        case 'ALL':
          return translations.all_travellers as string;
        case 'BUSINESS':
          return translations.business as string;
        case 'COUPLE':
          return translations.couples as string;
        case 'FAMILY':
          return translations.families as string;
        case 'SOLO':
          return translations.solo as string;
        default:
          return '';
      }
    },
    [translations],
  );

  useEffect(() => {
    if (!!reviews.length) {
      setSelectedReview(reviews[0]);
    } else {
      setSelectedReview({} as HotelReview);
    }
  }, [reviews]);

  return (
    <div className={clsx(styles.container, className)}>
      {!isHidingTravellerType ? (
        <div className={styles.reviewTabs}>
          {reviews.map((review) => (
            <div
              key={review.reviewerGroup}
              className={clsx(
                styles.reviewTab,
                review.reviewerGroup === selectedReview.reviewerGroup && styles.selected,
              )}
              onClick={() => setSelectedReview(review)}
            >
              {getReviewerGroupTranslation(review.reviewerGroup)}
            </div>
          ))}
        </div>
      ) : null}

      <div className={styles.selectedReview}>
        <div className={styles.categorySnippetsContainer}>
          <div className={clsx(styles.scoreContainer, backgroundColor)}>
            <div className={clsx(styles.progressRingContainer, typeColor)}>
              <ProgressRing size={120} thickness={4} progressPercentage={selectedReview.score} />

              <div className={styles.score}>
                {translateNumber(getReviewScoreDisplay(selectedReview.score), locale === 'fa')}
              </div>
            </div>

            <div className={styles.ratingTypeContainer}>
              <div className={styles.ratingType}>
                {getReviewRatingTypeDisplay(selectedReview.score, translations)}
              </div>
              <div className={styles.reviewBasedOnLabel}>
                {translateText(
                  translations.based_on_reviews,
                  locale,
                  getReviewerGroupTranslation(selectedReview.reviewerGroup),
                )}
              </div>
              <div className={styles.reviewCount}>
                {translateText(translations.count_review, locale, selectedReview.count)}
              </div>
            </div>
          </div>
        </div>

        {goodToKnowSnippets.length ? (
          <div className={styles.goodToKnowSnippets}>
            <div className={styles.label}>
              {((translations.good_to_know as string) || '').toLowerCase()}
            </div>

            {goodToKnowSnippets.map((goodToKnowSnippet) => (
              <div
                key={goodToKnowSnippet.id}
                className={clsx(
                  styles.goodToKnowSnippet,
                  !!goodToKnowSnippet.sentiment &&
                    styles[goodToKnowSnippet.sentiment.toLowerCase()],
                )}
              >
                {goodToKnowSnippet.sentiment?.toUpperCase() === 'POSITIVE' ? (
                  <SentimentSatisfied className={styles.icon} />
                ) : null}
                {goodToKnowSnippet.sentiment?.toUpperCase() === 'NEUTRAL' ? (
                  <SentimentNeutralIcon className={styles.icon} />
                ) : null}
                {goodToKnowSnippet.sentiment?.toUpperCase() === 'NEGATIVE' ? (
                  <SentimentDissatisfiedIcon className={styles.icon} />
                ) : null}

                <div className={styles.text}>{goodToKnowSnippet.text}</div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AiReviews;
